import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { makePrivateRequest, makeRequest} from 'core/utils/request';
import { toast } from 'react-toastify';
import { Category } from 'core/types/product';
import BaseForm from '../../BaseForm';
import Select from 'react-select'
import PriceField from './PriceField';
import './styles.scss';
import ImageUpload from '../ImageUpload';

export type FormState = {
    name: string;
    price: string;
    description: string;
    imgUrl: string;
    categories: Category[];
}

type ParamsType = {
    productId: string;
}

const Form = () => {
    const { register, handleSubmit, formState: { errors }, setValue, control } = useForm<FormState>();
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [productImgUrl, setProductImgUrl] = useState('');
    const { productId } = useParams<ParamsType>();
    const navigate = useNavigate();
    const isEditing = productId !== 'create';

    useEffect(() => {
        if (isEditing) {
            makePrivateRequest({ url: `/products/${productId}` })
                .then(response => {
                    const product = response.data;
                    setValue('name', product.name);
                    setValue('price', product.price);
                    setValue('description', product.description);
                    setValue('imgUrl', product.imgUrl);
                    setValue('categories', product.categories);
                    setProductImgUrl(product.imgUrl);
                })
        }
    }, [productId, isEditing, setValue]);

    useEffect(() => {
        setIsLoadingCategories(true);
        makeRequest({ url: '/categories' })
            .then(response => setCategories(response.data.content))
            .finally(() => setIsLoadingCategories(false));
    }, []);

    const onSubmit = (data: FormState) => {
        const payload = {
            ...data,
            imgUrl: productImgUrl
        }

        makePrivateRequest({
            url: isEditing ? `/products/${productId}` : '/products',
            method: isEditing ? 'PUT' : 'POST',
            data: payload
        })
            .then(() => {
                toast.info('Produto salvo com sucesso!');
                navigate('../');
            })
            .catch(() => {
                toast.error('Erro ao salvar produto!');
            });
    }

    const onUploadSuccess = (imgUrl: string) => {
        setProductImgUrl(imgUrl);
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <BaseForm title={isEditing ? 'Editar produto' : 'Cadastrar um produto'}>
                <div className="row">
                    <div className="col-6">
                        <div className="margin-bottom-30">
                            <input
                                {...register('name', {
                                    required: 'Campo obrigatório',
                                    minLength: { value: 5, message: 'O campo deve ter no mínimo 5 caracteres' },
                                    maxLength: { value: 60, message: 'O campo deve ter no máximo 60 caracteres' },
                                })}
                                name="name"
                                type="text"
                                className={`form-control input-base ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Nome do produto"
                            />
                            {errors.name && (
                                <div className="invalid-feedback d-block">
                                    {errors.name.message}
                                </div>
                            )}
                        </div>
                        <div className="margin-bottom-30">
                            <Controller
                                name="categories"
                                rules={{ required: 'Campo obrigatório' }}
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={categories}
                                        getOptionLabel={(option: Category) => option.name}
                                        getOptionValue={(option: Category) => String(option.id)}
                                        className={`${errors.categories ? 'is-invalid' : ''}`}
                                        classNamePrefix="categories-select"
                                        placeholder="Categorias"
                                        isMulti
                                        isLoading={isLoadingCategories}
                                    />
                                )}
                            />
                            {errors.categories && (
                                <div className="invalid-feedback d-block">
                                    {errors.categories.message}
                                </div>
                            )}
                        </div>
                        <div className="margin-bottom-30">
                            <PriceField control={control} />
                            {errors.price && (
                                <div className="invalid-feedback d-block">
                                    {errors.price.message}
                                </div>
                            )}
                        </div>
                        <div className="margin-bottom-30">
                            <ImageUpload
                                onUploadSuccess={onUploadSuccess}
                                productImgUrl={productImgUrl}
                            />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="margin-bottom-30">
                            <textarea
                                {...register('description', {
                                    required: 'Campo obrigatório',
                                    minLength: { value: 10, message: 'O campo deve ter no mínimo 10 caracteres' },
                                    maxLength: { value: 1000, message: 'O campo deve ter no máximo 1000 caracteres' },
                                })}
                                name="description"
                                className={`form-control input-base ${errors.description ? 'is-invalid' : ''}`}
                                cols={30}
                                rows={10}
                            />
                            {errors.description && (
                                <div className="invalid-feedback d-block">
                                    {errors.description.message}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </BaseForm>
        </form>
    )
}

export default Form;